import {
  AgentDefinitionStatus,
  type ChatCitedDefinitionsQuery,
} from "src/graphql";
import {
  type AgentDefinition,
  DEFINITION_STATUS_LABELS,
} from "src/pages/context-hub/definitions/utils";
import {
  type CitedDefinitionEntry,
  citedEntryDefinitionId,
  citedEntryMarkdown,
  citedEntryTitle,
  citedEntryVersion,
} from "./derive-session-context";

/** "deleted" is a cited id whose definition no longer resolves. */
export type CitationStatus = AgentDefinition["status"] | "deleted";

export const CITATION_STATUS_LABELS: Record<CitationStatus, string> = {
  assumed: DEFINITION_STATUS_LABELS.assumed,
  verified: DEFINITION_STATUS_LABELS.verified,
  deleted: "Deleted",
};

export const CITATION_STATUS_DOT_COLORS: Record<CitationStatus, string> = {
  [AgentDefinitionStatus.Assumed]: "warning.base",
  [AgentDefinitionStatus.Verified]: "success.base",
  deleted: "text.secondary",
};

export const CITATION_STATUS_TEXT_COLORS: Record<CitationStatus, string> = {
  [AgentDefinitionStatus.Assumed]: "text.warning",
  [AgentDefinitionStatus.Verified]: "text.success",
  deleted: "text.secondary",
};

export type CitedAgentDefinitionVersion =
  ChatCitedDefinitionsQuery["agentDefinitionVersions"][number];

export type DefinitionsLookup = {
  definitionsById: ReadonlyMap<string, AgentDefinition>;
  citedVersionsById: ReadonlyMap<string, CitedAgentDefinitionVersion>;
  /** Whether the latest fetch failed. A missing definition doesn't mean it was deleted when the fetch failed. */
  fetchFailed: boolean;
};

export type CitationContent = {
  title: string;
  markdown: string;
  usageGuidance: string | null;
};

/** What the agent cited, beside the corresponding stored definition when one resolves. */
export type ResolvedCitation = {
  citedEntry: CitedDefinitionEntry;
  definition?: AgentDefinition;
  /** The version the turn was served, else the current version for an entry with no stamped version, else what the agent cited. */
  content: CitationContent;
  /** Whether the content matches the definition's current version. */
  showsCurrentVersion: boolean;
  status: CitationStatus;
};

export function resolveCitation(
  citedEntry: CitedDefinitionEntry,
  { definitionsById, citedVersionsById, fetchFailed }: DefinitionsLookup,
): ResolvedCitation {
  const definitionId = citedEntryDefinitionId(citedEntry);
  const definition = definitionId
    ? definitionsById.get(definitionId)
    : undefined;
  const stampedVersion = citedEntryVersion(citedEntry);
  const servedVersion = stampedVersion
    ? citedVersionsById.get(stampedVersion.versionId)
    : undefined;
  // An approval saves a new version even when it changes nothing.
  // In this case, the served version still matches the current version.
  const servedVersionMatchesCurrent =
    definition !== undefined &&
    servedVersion !== undefined &&
    servedVersion.name === definition.name &&
    servedVersion.markdown === definition.markdown &&
    servedVersion.usageGuidance === definition.usageGuidance;
  const showsCurrentVersion =
    definition !== undefined &&
    (!stampedVersion ||
      stampedVersion.versionId === definition.latestVersionId ||
      servedVersionMatchesCurrent);

  let content: CitationContent;
  if (servedVersion) {
    content = {
      title: servedVersion.name,
      markdown: servedVersion.markdown,
      usageGuidance: servedVersion.usageGuidance ?? null,
    };
  } else if (definition && showsCurrentVersion) {
    content = {
      title: definition.name,
      markdown: definition.markdown,
      usageGuidance: definition.usageGuidance ?? null,
    };
  } else {
    content = {
      title: citedEntryTitle(citedEntry),
      markdown: citedEntryMarkdown(citedEntry),
      usageGuidance: null,
    };
  }

  if (!definition) {
    return {
      citedEntry,
      content,
      showsCurrentVersion,
      status: citationStatusWithoutDefinition(citedEntry, fetchFailed),
    };
  }
  // An approval that kept the cited markdown counts as the agent having cited the approved definition.
  const citesCurrentStatus =
    !stampedVersion ||
    stampedVersion.versionId === definition.latestVersionId ||
    stampedVersion.versionId === definition.approvedFromVersionId;
  return {
    citedEntry,
    definition,
    content,
    showsCurrentVersion,
    status: citesCurrentStatus ? definition.status : stampedVersion.status,
  };
}

export function resolveCitations(
  citedEntries: readonly CitedDefinitionEntry[],
  definitionsLookup: DefinitionsLookup,
): ResolvedCitation[] {
  return citedEntries.map((citedEntry) =>
    resolveCitation(citedEntry, definitionsLookup),
  );
}

// A cited id missing from a successful fetch was deleted. Otherwise the status falls back to what the entry was cited as.
function citationStatusWithoutDefinition(
  citedEntry: CitedDefinitionEntry,
  fetchFailed: boolean,
): CitationStatus {
  if (!fetchFailed && citedEntryDefinitionId(citedEntry) !== null) {
    return "deleted";
  }
  if (citedEntry.kind === "assumption") return AgentDefinitionStatus.Assumed;
  // Definitions were all verified before they moved to context entities, so an older reference without a status was most likely verified.
  return citedEntry.entry.status ?? AgentDefinitionStatus.Verified;
}
